'use client'

import * as React from 'react'
import { useTranslations } from 'next-intl'
import { Check, ChevronsUpDown, XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import LazyImage from '@/components/partials/lazy-image'

export interface ComboboxOption {
  label: string
  value: string
  searchValue?: string
  description?: string
  disabled?: boolean
  level?: number
  icon?: React.ReactNode
  image?: string
}

interface ComboboxProps {
  options: ComboboxOption[]
  value?: string | string[]
  onValueChange?: (value: string | string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  icon?: React.ReactNode
  multiple?: boolean
  clearable?: boolean
  error?: boolean
  'aria-invalid'?: boolean
  onSearchChange?: (search: string) => void
  id?: string
  container?: HTMLElement | null
}

export function Combobox({
  options = [],
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  disabled = false,
  className,
  icon,
  multiple = false,
  clearable = false,
  error = false,
  'aria-invalid': ariaInvalid,
  onSearchChange,
  id,
  container
}: ComboboxProps) {
  const t = useTranslations()
  const listId = React.useId()
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setSearch('')
    }
    setOpen(next)
  }

  const valueArray = multiple ? (value && typeof value === 'object' && 'length' in value ? value : []) : []
  const selectedOption = !multiple ? options.find(option => option.value === value) : null
  const selectedCount = multiple ? valueArray.length : 0

  // Check if there's a value to clear
  const hasValue = multiple ? valueArray.length > 0 : value && value !== '' && value !== undefined

  const isInvalid = error || !!ariaInvalid

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          role="combobox"
          aria-controls={listId}
          aria-expanded={open}
          aria-invalid={isInvalid || undefined}
          disabled={disabled}
          className={cn(
            'border-input dark:bg-input/30 flex h-9 w-full cursor-pointer items-center justify-between rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs transition-[color,box-shadow] outline-none disabled:cursor-not-allowed disabled:opacity-50',
            'focus:border-ring focus:ring-ring/50 focus:ring-[3px]',
            'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
            !value && 'text-muted-foreground',
            className
          )}>
          <div className="flex items-center gap-2 truncate">
            {icon}
            {multiple ? (
              selectedCount > 0 ? (
                `${selectedCount} ${t('selected')}`
              ) : (
                placeholder || t('select')
              )
            ) : selectedOption ? (
              <>
                {selectedOption.image && (
                  <div className="relative h-6 w-6 shrink-0 overflow-hidden rounded">
                    <LazyImage
                      src={selectedOption.image}
                      alt={selectedOption.label}
                      fill
                      sizes="24px"
                      className="object-contain"
                    />
                  </div>
                )}
                {selectedOption.icon && !selectedOption.image && (
                  <div className="h-5 w-5 shrink-0">{selectedOption.icon}</div>
                )}
                <div className="flex min-w-0 flex-col items-start">
                  <span className="truncate">{selectedOption.label}</span>
                  {selectedOption.description && (
                    <span className="text-muted-foreground truncate text-xs">{selectedOption.description}</span>
                  )}
                </div>
              </>
            ) : (
              placeholder || t('select')
            )}
          </div>
          <div className="flex items-center gap-1">
            {clearable && hasValue && (
              <div
                className="mr-1 flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full bg-gray-100 text-gray-500 opacity-70 hover:opacity-100"
                onClick={e => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (multiple) {
                    onValueChange?.([])
                  } else {
                    onValueChange?.('')
                  }
                }}
                onMouseDown={e => {
                  e.preventDefault()
                  e.stopPropagation()
                }}>
                <XIcon className="h-3 w-3" />
              </div>
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        align="start"
        container={container}
        style={{
          minWidth: 'var(--radix-popper-anchor-width)',
          width: 'max-content'
        }}>
        <Command shouldFilter={!onSearchChange}>
          <CommandInput
            placeholder={searchPlaceholder || t('search')}
            value={search}
            onValueChange={value => {
              setSearch(value)
              onSearchChange?.(value)
            }}
          />
          <CommandList
            id={listId}
            className="max-h-75 scroll-py-1 overflow-x-hidden overflow-y-auto"
            onWheel={e => e.nativeEvent.stopPropagation()}>
            <CommandEmpty>{emptyText || t('not_found')}</CommandEmpty>
            <CommandGroup>
              {options.map(option => (
                <CommandItem
                  key={option.value}
                  value={option.searchValue || option.value}
                  disabled={option.disabled}
                  onSelect={currentValue => {
                    const selectedOption = options.find(opt => (opt.searchValue || opt.value) === currentValue)
                    if (selectedOption) {
                      if (multiple) {
                        const currentValues = value && typeof value === 'object' && 'length' in value ? value : []
                        const newValues = currentValues.includes(selectedOption.value)
                          ? currentValues.filter(v => v !== selectedOption.value)
                          : [...currentValues, selectedOption.value]
                        onValueChange?.(newValues)
                      } else {
                        const newValue = selectedOption.value === value ? '' : selectedOption.value
                        onValueChange?.(newValue)
                        setOpen(false)
                      }
                    }
                  }}>
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      multiple
                        ? valueArray.includes(option.value)
                          ? 'opacity-100'
                          : 'opacity-0'
                        : value === option.value
                          ? 'opacity-100'
                          : 'opacity-0'
                    )}
                  />
                  {option.image && (
                    <div className="relative mr-1 h-6 w-6 shrink-0 overflow-hidden rounded">
                      <LazyImage src={option.image} alt={option.label} fill sizes="24px" className="object-contain" />
                    </div>
                  )}
                  {option.icon && !option.image && (
                    <div className="mr-1.5 ml-0.5 flex h-5 w-5 shrink-0 items-center justify-center">{option.icon}</div>
                  )}
                  <div className="flex min-w-0 flex-col items-start">
                    <span className="truncate" style={{ paddingLeft: option.level ? `${option.level * 18}px` : '0' }}>
                      {option.label}
                    </span>
                    {option.description && (
                      <span className="text-muted-foreground truncate text-xs">{option.description}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
